import { config } from '_appConfig'

export class ApiError extends Error {
  public response?: Response
  public code?: string
  public status?: number

  constructor(message: string, response?: Response, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.response = response
    this.code = code
    this.status = response?.status
  }
}

export const isContentSpaceNotFoundError = (error: unknown): boolean =>
  error instanceof ApiError && (error.code === 'CONTENT_SPACE_NOT_FOUND' || error.status === 404)

// Backend (`serveV2Spa`) injects pre-resolved REST payloads into HTML as
// `window.__PORTAL_BOOT__`, keyed by exact api-client URL path. Hydrate once
// on first module load, then drop the global so accidental late reads don't
// see stale payloads — this is one-shot bootstrap, not a cache.
const bootCache = new Map<string, unknown>()
const win =
  typeof window !== 'undefined'
    ? (window as Window & { __PORTAL_BOOT__?: Record<string, unknown> })
    : undefined
if (win?.__PORTAL_BOOT__) {
  for (const key of Object.keys(win.__PORTAL_BOOT__)) {
    bootCache.set(key, win.__PORTAL_BOOT__[key])
  }
  delete win.__PORTAL_BOOT__
}

class ContentSpaceApiClient {
  private baseUrl: string
  // Coalesces concurrent GETs to the same URL into one network roundtrip —
  // covers React 18 StrictMode double-mount and any sibling components racing
  // for the same payload.
  private inflightGets = new Map<string, Promise<unknown>>()

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }))
      throw new ApiError(
        errorData.error || errorData.message || 'Request failed',
        response,
        errorData.code,
      )
    }

    const contentType = response.headers.get('content-type')
    if (contentType?.includes('text/plain')) {
      return response.text() as unknown as T
    }

    return response.json()
  }

  async get<T>(url: string): Promise<T> {
    if (bootCache.has(url)) {
      const value = bootCache.get(url) as T
      bootCache.delete(url)
      return value
    }
    const existing = this.inflightGets.get(url)
    if (existing) {
      return existing as Promise<T>
    }
    const promise = this.request<T>(url, { method: 'GET' })
    this.inflightGets.set(url, promise)
    promise.finally(() => this.inflightGets.delete(url))
    return promise
  }

  async post<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      signal,
    })
  }

  async patch<T>(url: string, body?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T>(url: string): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' })
  }

  async upload<T>(url: string, formData: FormData): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Failed to upload file',
      }))
      throw new ApiError(errorData.error || 'Upload failed', response, errorData.code)
    }

    const contentType = response.headers.get('content-type')
    if (contentType?.includes('text/plain')) {
      return response.text() as unknown as T
    }

    return response.json()
  }
}

export const apiClient = new ContentSpaceApiClient(config.io.contentspaceEndpoint)
